import { type ApiFieldError } from '@store/error';

type Translate = (key: string) => string;

/**
 * The server's refusals of the settings, by their code. A code not listed here (zod's own, such
 * as `invalid_string`) is said as "not a valid value", and one with no code in the server's words.
 */
const messageKeys: Record<string, string> = {
  not_origin: 'ServerConfig_Sso_Error_NotOrigin',
  https_required: 'ServerConfig_Sso_Error_HttpsRequired',
  microsoft_without_tenant: 'ServerConfig_Sso_Error_MicrosoftWithoutTenant',
  openid_required: 'ServerConfig_Sso_Error_OpenidRequired',
  required_when_enabled: 'ServerConfig_Sso_Error_RequiredWhenEnabled',
  secret_unreadable: 'ServerConfig_Sso_Secret_Unreadable_Description',
  no_rules: 'ServerConfig_Sso_Error_NoRules',
  team_missing: 'ServerConfig_Sso_Error_TeamMissing',
  // zod's, for a field left empty: a rule without a value.
  too_small: 'ServerConfig_Sso_Error_Required',
  // The form's own, before sending: a rule that gives neither a role nor a team.
  rule_gives_nothing: 'ServerConfig_Sso_Error_RuleGivesNothing',
};

export const ssoErrorText = (t: Translate, error: ApiFieldError): string => {
  if (error.code && messageKeys[error.code]) return t(messageKeys[error.code]!);

  return error.code ? t('ServerConfig_Sso_Error_Invalid') : error.message;
};

/** The label of the field a path names, for a refusal said away from its field. */
const labelKeys: Record<string, string> = {
  provider: 'ServerConfig_Sso_Provider',
  displayName: 'ServerConfig_Sso_DisplayName',
  publicUrl: 'ServerConfig_Sso_PublicUrl',
  tenantId: 'ServerConfig_Sso_TenantId',
  issuer: 'ServerConfig_Sso_Issuer',
  clientId: 'ServerConfig_Sso_ClientId',
  clientSecret: 'ServerConfig_Sso_ClientSecret',
  clientSecretExpiresAt: 'ServerConfig_Sso_ClientSecretExpiresAt',
  scopes: 'ServerConfig_Sso_Scopes',
  tokenAuthMethod: 'ServerConfig_Sso_TokenAuthMethod',
  claims: 'ServerConfig_Sso_Claims',
  requireVerifiedEmail: 'ServerConfig_Sso_RequireVerifiedEmail',
  accounts: 'ServerConfig_Sso_Section_Accounts',
};

export const ssoFieldLabel = (t: Translate, path: string): string => {
  const key = labelKeys[path.split('.')[0] ?? ''];

  return key ? t(key) : path;
};

/** The first refusal of `path`, or of a path under it unless `exact`. */
export const errorAt = (errors: ApiFieldError[], path: string, { exact = false } = {}): ApiFieldError | undefined =>
  errors.find((error) => error.path === path || (!exact && error.path.startsWith(`${path}.`)));
